import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { createBaseQuery } from './utils/createBaseQuery';
import { IUsersStatsResponse } from '../../common/interfaces/UsersStatsResponse.interface';
import { IOnboardingQuestionnaireStatsResponse } from '../../common/interfaces/OnboardingQuestionnaireStatsResponse.interface';
import { IOnboardingQuestionnaireQuestionsAnalyticsResponse } from '../../common/interfaces/OnboardingQuestionnaireQuestionsAnalyticsResponse.interface';
import { IWeeklyLogAnalyticsResponse } from '../../common/interfaces/WeeklyLogAnalyticsResponse.interface';

export const statisticsApi = createApi({
  reducerPath: 'statisticsApi',
  baseQuery: createBaseQuery(''),
  tagTypes: ['Statistics'],
  endpoints: (builder) => ({
    fetchUsersStats: builder.query<
      { code: HttpStatus; data: IUsersStatsResponse },
      void
    >({
      query: () => ({
        url: '/users/stats',
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Statistics' as const, id: 'LIST' }]
          : [{ type: 'Statistics', id: 'LIST' }],
    }),

    fetchOnboardingQuestionnaireStats: builder.query<
      { code: HttpStatus; data: IOnboardingQuestionnaireStatsResponse[] },
      void
    >({
      query: () => ({
        url: '/onboarding/questionnaires/completion-stats',
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Statistics' as const, id: 'LIST' }]
          : [{ type: 'Statistics', id: 'LIST' }],
    }),

    fetchOnboardingQuestionnaireQuestionsAnalytics: builder.query<
      {
        code: HttpStatus;
        data: IOnboardingQuestionnaireQuestionsAnalyticsResponse;
      },
      { questionnaireId: string }
    >({
      query: ({ questionnaireId }) => ({
        url: `/onboarding/questionnaires/analytics/?questionnaireId=${questionnaireId}`,
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Statistics' as const, id: 'LIST' }]
          : [{ type: 'Statistics', id: 'LIST' }],
    }),

    fetchUsersWeekLogAnalytics: builder.query<
      { code: HttpStatus; data: IWeeklyLogAnalyticsResponse },
      {
        startDate?: string;
        endDate?: string;
        questionnaireId?: string;
        questionId?: string;
        answerIds?: string[];
      }
    >({
      query: ({
        startDate,
        endDate,
        questionnaireId,
        questionId,
        answerIds,
      }) => {
        const params = new URLSearchParams();

        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (questionnaireId) params.append('questionnaireId', questionnaireId);
        if (questionId) params.append('questionId', questionId);
        if (answerIds && answerIds.length > 0) {
          answerIds.forEach((id) => params.append('answerIds', id));
        }

        return {
          url: `/users/week-log-analytics?${params.toString()}`,
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (result) =>
        result
          ? [{ type: 'Statistics' as const, id: 'LIST' }]
          : [{ type: 'Statistics', id: 'LIST' }],
    }),
  }),
});

export const {
  useFetchUsersStatsQuery,
  useFetchOnboardingQuestionnaireStatsQuery,
  useFetchOnboardingQuestionnaireQuestionsAnalyticsQuery,
  useFetchUsersWeekLogAnalyticsQuery,
} = statisticsApi;
